/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import EditForm from './EditForm'
import { toast } from '../../../../services/toast'
import type { FolderStructureTemplateType } from '../../../../services/api/api.folderstructuretemplatetypes'

interface ModalEditProps {
  folderStrctTplType?: FolderStructureTemplateType
  onClose: () => void
  onSuccess?: () => void
}

const ModalEdit: React.FC<ModalEditProps> = ({ folderStrctTplType, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableContext = useContext(RcmTableDataContext)

  const [isEdit, setIsEdit] = useState(!!folderStrctTplType)
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState<Promise<any> | null>(null)
  const [typeData, setTypeData] = useState<any>({})

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    if (isEdit && folderStrctTplType?.id) {
      const promise = context.api.folderStructureTemplateTypes.getById(folderStrctTplType.id, true)
      setLoading(promise)

      try {
        const data = await promise
        setTypeData(data)
      } catch (error) {
        console.error('Error loading folder structure template type:', error)
      } finally {
        setLoading(null)
      }
    }
  }

  const doSave = async (values: any) => {
    try {
      let apiCall: Promise<any>

      if (isEdit) {
        apiCall = context.api.folderStructureTemplateTypes.update(values.id, values, true)
      } else {
        apiCall = context.api.folderStructureTemplateTypes.create(values, true)
      }

      setLoading(apiCall)

      await apiCall

      toast.success(
        translate(
          isEdit ? 'FOLDER_STUCTURE_TPL_TYPES_UPDATE_SUCCESS_MSG' : 'FOLDER_STUCTURE_TPL_TYPES_CREATE_SUCCESS_MSG'
        )
      )

      // Refresh the table
      if (tableContext?.refreshTable) {
        tableContext.refreshTable()
      }

      if (onSuccess) {
        onSuccess()
      }

      doClose()
    } catch (error: any) {
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')
      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }
      toast.error(errorMsg)
    } finally {
      setLoading(null)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(isEdit ? 'FOLDER_STUCTURE_TPL_TYPES_EDIT_TITLE' : 'FOLDER_STUCTURE_TPL_TYPES_ADD_TITLE')}
      size="md"
      dataTestId="edit-folder-strct-tpl-type-modal"
    >
      <EditForm onSubmit={doSave} doClose={doClose} isEdit={isEdit} folderStrctTplType={typeData} loading={loading} />
    </Modal>
  )
}

export default ModalEdit
